import IconAlertInfo from "@shared/components/icons/AlertInfo"
import IconCircleCheck from "@shared/components/icons/CircleCheck"
import IconCircleNoCheck from "@shared/components/icons/CircleNoCheck"
import IconDragDots from "@shared/components/icons/DragDots"
import { useDocument, useDocumentsTogether } from "@shared/components/providers/SupergrainProvider"
import { cx } from "@shared/components/untitledui/util/cx"
import { tracked } from "@supergrain/kernel/react"
import planbookCourseToggleAction from "cc-frontend/lib/actions/app-state/PLANBOOK_COURSE_TOGGLE"
import planbookLineUpClassesAction from "cc-frontend/lib/actions/app-state/PLANBOOK_LINE_UP_CLASSES"
import PLANBOOK_CHANGE_COURSE_ORDER from "cc-frontend/lib/actions/planbook/PLANBOOK_CHANGE_COURSE_ORDER"
import type Dispatcher from "cc-frontend/services/dispatcher"
import ReorderableList, { ReorderableItem } from "react-app/components/util/ReorderableList"
import Toggle from "react-app/components/util/Toggle"
import { useEmberService } from "react-app/providers/ember-service-context"
import { Button } from "react-aria-components"
import styles from "./FilterClasses.module.css"

interface Props {
  planbookId: string
  hiddenCourseIds: string[]
  toggleCourseId: (courseId: string) => void
  showAllCourses: () => void
  hideAllCourses: () => void
  classesLinedUp: boolean
  toggleClassesLinedUp: () => void
  hideClassesLinedUpToggle: boolean
  lessonsCollapsed: boolean
  toggleCollapseLessons: () => void
}

/** The Rearrange & Filter menu's body. */
export default tracked(function FilterClasses({
  planbookId,
  hiddenCourseIds,
  toggleCourseId,
  showAllCourses,
  hideAllCourses,
  classesLinedUp,
  toggleClassesLinedUp,
  hideClassesLinedUpToggle,
  lessonsCollapsed,
  toggleCollapseLessons,
}: Props) {
  const dispatcher = useEmberService<Dispatcher>("dispatcher")
  const courseIds = (
    useDocument("planbook", planbookId).value?.relationships.courses.data ?? []
  ).map((datum) => datum.id)
  const courses = useDocumentsTogether("course", courseIds).value ?? []

  return (
    <>
      {!hideClassesLinedUpToggle && (
        <>
          <div className={styles["toggle-line-up-classes-row"]}>
            <div className={styles["toggle-line-up-classes-row__text"]}>Line Up Classes</div>
            <div
              className={styles["toggle-line-up-classes-row__toggle"]}
              data-test-lineup-class-toggle
            >
              <Toggle
                aria-label="Line Up Classes"
                isOn={classesLinedUp}
                onToggle={() => {
                  toggleClassesLinedUp()
                  dispatcher.dispatchPreparedAction(
                    planbookLineUpClassesAction({ planbookId, classesLinedUp: !classesLinedUp }),
                  )
                }}
              />
            </div>
          </div>
          {classesLinedUp ? (
            <div className={styles["toggle-classes-instructions"]}>
              <IconAlertInfo />
              <div className={styles["toggle-classes-instructions__text"]}>
                <div className={styles["toggle-classes-instructions__header"]}>
                  Want to change the order?
                </div>
                <div className={styles["toggle-classes-instructions__subheader"]}>
                  Click & drag the dots on the classes below.
                </div>
              </div>
            </div>
          ) : (
            <div className={styles["toggle-line-up-classes-divider"]} />
          )}
        </>
      )}
      <div className={styles["toggle-line-up-classes-row"]}>
        <div className={styles["toggle-line-up-classes-row__text"]}>Collapse Classes</div>
        <div
          className={styles["toggle-line-up-classes-row__toggle"]}
          data-test-collapse-class-toggle
        >
          <Toggle
            aria-label="Collapse Classes"
            isOn={lessonsCollapsed}
            onToggle={toggleCollapseLessons}
          />
        </div>
      </div>
      <div className={styles["toggle-line-up-classes-divider"]} />
      <div className={styles["toggle-line-up-classes-row"]}>
        <div className={styles["toggle-line-up-classes-row__text"]}>Filter Classes</div>
        <div className={styles.controls}>
          <div className={styles.controls__inner}>
            <Button className={styles.controls__control} onPress={hideAllCourses}>
              Hide all Classes
            </Button>
            <Button className={styles.controls__control} onPress={showAllCourses}>
              Show all classes
            </Button>
          </div>
        </div>
      </div>
      <ReorderableList
        aria-label="Classes"
        className={styles["class-list"]}
        items={courses}
        onReorder={(reordered) =>
          dispatcher.dispatchAction(PLANBOOK_CHANGE_COURSE_ORDER, {
            courseIds: reordered,
            planbookId,
          })
        }
        dependencies={[hiddenCourseIds]}
        onAction={(key) => {
          const courseId = String(key)
          dispatcher.dispatchPreparedAction(
            planbookCourseToggleAction({
              planbookId,
              courseId,
              isTogglingOn: hiddenCourseIds.includes(courseId),
            }),
          )
          toggleCourseId(courseId)
        }}
      >
        {(course) => {
          const isHidden = hiddenCourseIds.includes(course.id)
          return (
            <ReorderableItem
              id={course.id}
              textValue={course.attributes.title}
              data-color={course.attributes.color}
              className="set-course-color-vars"
            >
              <div
                className={cx(
                  styles["class-list__class"],
                  isHidden ? "bg-white" : "take-course-background-color is-selected",
                )}
              >
                <div className={styles["class-list__class__left-side"]}>
                  <Button
                    slot="drag"
                    aria-label={`Move ${course.attributes.title}`}
                    className={styles["class-list__class__move-button"]}
                  >
                    <IconDragDots />
                  </Button>
                  <div
                    className={cx(
                      styles["class-list__class__title"],
                      isHidden ? "is-not-selected" : "is-selected",
                    )}
                  >
                    {course.attributes.title}
                  </div>
                </div>
                <div className="class-list__class__right-side">
                  <div
                    data-test-filter-course-id={course.id}
                    data-test-is-selected={isHidden}
                    className={cx(
                      styles["class-list__class__checkbox"],
                      isHidden ? "is-not-selected" : "is-selected",
                    )}
                  >
                    {isHidden ? <IconCircleNoCheck /> : <IconCircleCheck />}
                  </div>
                </div>
              </div>
            </ReorderableItem>
          )
        }}
      </ReorderableList>
    </>
  )
})
